import { ModalWindow } from '@cian/ui-kit';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { trackCloseNoLinksPopup, trackRemoveFromSpamClick, trackResendErrorMessageClick } from './tracking';
import { setEditableMessage } from '../../actions/chat/actions';
import { removeFromArchive, removeFromSpam } from '../../actions/chatOperations';
import { Alert } from '../../components/Alert';
import { NoLinksPopup } from '../../components/NoLinksPopup';
import { chatListSelector, currentChatOpponentIdSelector } from '../../selectors';
import { alertSelector } from '../../selectors/alertSelector';
import { selectIsBottomNavigationEnabled } from '../../selectors/experiments/selectIsBottomNavigationExperimentEnabled';
import { hideAlert } from '../../slices/alertSlice';
import { EAlert } from '../../types/alert';

export const AlertContainer: React.FC = React.memo(() => {
  const dispatch = useDispatch();

  const { movedToArchiveChatIds, movedToSpamChatIds } = useSelector(chatListSelector);
  const { isVisible, type, message, chatId } = useSelector(alertSelector);
  const opponentId = useSelector(currentChatOpponentIdSelector);
  const isBottomNavigationEnabled = useSelector(selectIsBottomNavigationEnabled);

  const onCloseHandler = React.useCallback(() => dispatch(hideAlert()), [dispatch]);

  const removeFromArchiveHandler = React.useCallback(() => {
    dispatch(removeFromArchive(movedToArchiveChatIds[movedToArchiveChatIds.length - 1]));
    dispatch(hideAlert());
    trackRemoveFromSpamClick('archive', opponentId);
  }, [dispatch, movedToArchiveChatIds, opponentId]);

  const removeFromSpamHandler = React.useCallback(() => {
    const chatId = movedToSpamChatIds[movedToSpamChatIds.length - 1];

    dispatch(removeFromSpam(chatId));
    dispatch(hideAlert());
    trackRemoveFromSpamClick('spam', opponentId);
  }, [dispatch, movedToSpamChatIds, opponentId]);

  /* istanbul ignore next */
  const resendErrorMessage = React.useCallback(() => {
    dispatch(setEditableMessage({ text: message as string, chatId: chatId as string }));
    dispatch(hideAlert());
    trackResendErrorMessageClick(opponentId);
  }, [chatId, dispatch, message, opponentId]);

  const closeNoLinksPopup = React.useCallback(() => {
    dispatch(setEditableMessage({ text: '', chatId: chatId as string }));
    dispatch(hideAlert());
    trackCloseNoLinksPopup(opponentId);
  }, [chatId, dispatch, opponentId]);

  if (type === 'spam') {
    return (
      <Alert
        isError={false}
        isVisible={isVisible}
        withOffset={isBottomNavigationEnabled}
        message="Чат заблокирован"
        onClose={onCloseHandler}
        onCancel={movedToSpamChatIds.length > 0 ? removeFromSpamHandler : undefined}
      />
    );
  }

  if (type === 'archive') {
    const message = movedToArchiveChatIds.length > 1 ? 'Чаты перенесены в архив' : 'Чат перенесён в архив';

    return (
      <Alert
        isError={false}
        isVisible={isVisible}
        withOffset={isBottomNavigationEnabled}
        message={message}
        onClose={onCloseHandler}
        onCancel={movedToArchiveChatIds.length > 0 ? removeFromArchiveHandler : undefined}
      />
    );
  }

  if (type === EAlert.MessageHasLinkToAnotherService) {
    return (
      <ModalWindow
        open
        content={<NoLinksPopup onClick={resendErrorMessage} />}
        onClose={closeNoLinksPopup}
        size="M"
        fixed={false}
      />
    );
  }

  return (
    <Alert
      isError={type !== 'info'}
      isVisible={isVisible}
      withOffset={isBottomNavigationEnabled}
      onClose={onCloseHandler}
      message={message || 'Сервисная ошибка. Повторите попытку'}
    />
  );
});

AlertContainer.displayName = 'AlertContainer';
